/**
 * WombFlow — Flow controller for Womb & Creative Centre healing
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HealingEntry from './HealingEntry';
import GenericArrival from './GenericArrival';
import PositioningGuide from './PositioningGuide';
import GenericAffirmationsPreview from './GenericAffirmationsPreview';
import GenericImmersion from './GenericImmersion';
import HealingCompletion from './HealingCompletion';
import WombVisualEngine from './WombVisualEngine';
import useAudioMode from '../../hooks/useAudioMode';

const CONDITION = 'womb-healing';
const CONDITION_LABEL = 'Womb & Creative Centre';

const ARRIVAL_TEXTS = [
  'Take a moment\u2026',
  'Let everything settle\u2026',
  'Bring your awareness down\u2026\nto the deep centre of your body',
  'You are here\u2026\nand this space is ready',
];

const CATEGORY_META = {
  safety_grounding: { label: 'Grounding into Safety', description: 'Feeling safe to soften here' },
  releasing_old_patterns: { label: 'Releasing Old Patterns', description: 'Letting go of what was stored' },
  nurturing_warmth: { label: 'Nurturing Warmth', description: 'Deep comfort and care' },
  creative_flow: { label: 'Creative Flow', description: 'Allowing what wants to emerge' },
  integration: { label: 'Trusting the Centre', description: 'Wholeness and peace' },
};
const CATEGORY_ORDER = ['safety_grounding', 'releasing_old_patterns', 'nurturing_warmth', 'creative_flow', 'integration'];

const TEXT_SYNC = [
  { t: 3, text: 'softly\u2026', dur: 3000 },
  { t: 10, text: 'gently\u2026', dur: 3000 },
  { t: 20, text: 'a deep warmth\u2026', dur: 3500 },
  { t: 35, text: 'grounded\u2026', dur: 3000 },
  { t: 50, text: 'held\u2026', dur: 3000 },
  { t: 68, text: 'nurturing\u2026', dur: 3000 },
  { t: 85, text: 'softening\u2026', dur: 3000 },
  { t: 105, text: 'flowing\u2026', dur: 3000 },
  { t: 130, text: 'allowing\u2026', dur: 3000 },
  { t: 155, text: 'creating\u2026', dur: 3000 },
  { t: 185, text: 'held\u2026', dur: 3000 },
  { t: 215, text: 'warm\u2026', dur: 3000 },
  { t: 245, text: 'nourished\u2026', dur: 3000 },
  { t: 275, text: 'deeply safe\u2026', dur: 3500 },
  { t: 310, text: 'this is yours\u2026', dur: 4000 },
  { t: 340, text: 'this remains\u2026', dur: 3500 },
  { t: 370, text: 'whole\u2026', dur: 3000 },
  { t: 390, text: 'rest\u2026', dur: 5000 },
];

const WombFlow = () => {
  const navigate = useNavigate();
  const [, setMode] = useAudioMode();
  const [step, setStep] = useState(0);
  const [affirmations, setAffirmations] = useState([]);

  return (
    <div className="fixed inset-0 bg-black" data-testid="womb-flow">
      {step === 0 && (
        <HealingEntry title={CONDITION_LABEL}
          subtitle="A deep warmth begins to gather…"
          description="gently nurturing the space within"
          onBegin={(mode) => { setMode(mode); setStep(3); }}
          onBack={() => navigate('/healing')}
          area="healing_womb"
          glow="rgba(228,170,90,0.60)"
          accent="rgba(216,167,143,0.62)" />
      )}
      {step === 1 && <GenericArrival texts={ARRIVAL_TEXTS} glowColor="rgba(200,155,85,0.3)" onContinue={() => setStep(2)} />}
      {step === 2 && <PositioningGuide onReady={() => setStep(3)} />}
      {step === 3 && (
        <GenericAffirmationsPreview condition={CONDITION} categoryMeta={CATEGORY_META} categoryOrder={CATEGORY_ORDER}
          title="Womb Affirmations" onContinue={(affs) => { setAffirmations(affs); setStep(4); }} />
      )}
      {step === 4 && (
        <GenericImmersion VisualEngine={WombVisualEngine} textSync={TEXT_SYNC}
          affirmations={affirmations} title="Womb Flow" onComplete={() => setStep(5)} />
      )}
      {step === 5 && (
        <HealingCompletion onSave={() => {}} onRepeat={() => setStep(0)}
          onHome={() => navigate('/healing')} homeLabel="Return to Healing" />
      )}
    </div>
  );
};

export default WombFlow;
